import accessDeniedIllustration from '../../shared/images/accessDenied.svg';
import {LABELS} from '../../shared/labels/Labels';
import {actions as actionCreators, XeroSignupAction} from '../actions';
import {DEFAULT_STATE, XeroSignupState} from '../state';
import ErrorScreen from './ErrorScreen/ErrorScreen';
import LoadingScreen from './LoadingScreen/LoadingScreen';
import OrganisationSelector from './OrganisationSelector/OrganisationSelector';
import SignupForm from './SignupForm/SignupForm';
import {
  BoardGroup,
  LegendText,
  StyledBoardHeading,
  StyledContainer,
} from './styled';
import Text from '@planday-corp/ui/text/Text';
import React, {useEffect} from 'react';
import {connect, ConnectedProps} from 'react-redux';
import {AnyAction, bindActionCreators, Dispatch} from 'redux';

export interface XeroSignupProps extends XeroSignupState<string> {
  actions: Pick<
    typeof actionCreators,
    | 'init'
    | 'receive'
    | 'updateField'
    | 'save'
    | 'fetchFormData'
    | 'setTenantId'
    | 'proceed'
    | 'redirectToPortal'
  >;
}

function XeroSignup({
  actions,
  tenants,
  tenantId,
  view,
  signupUrl,
  isPortalCreationInProgress,
}: PropsFromRedux) {
  useEffect(() => {
    const url = new URL(window.location.href);
    const signupRequestId = url.searchParams.get('signupRequestId') ?? '';
    const signupOrigin = url.searchParams.get('SignupOrigin') ?? undefined;
    const source = url.searchParams.get('SignupSource') ?? undefined;
    const error = url.searchParams.get('Error') ?? undefined;

    actions.init(signupRequestId, source, signupOrigin, error);
  }, []);

  return (
    <>
      {view === 'emptyView' ? (
        <></>
      ) : view === 'errorView' ? (
        <ErrorScreen
          redirectPath={signupUrl}
          illustrationSvg={accessDeniedIllustration}
          title={LABELS.errorScreenTitle}
          body={LABELS.errorScreenBody}
          buttonLabel={LABELS.errorScreenBtn}
        />
      ) : view === 'loadingView' ? (
        <LoadingScreen
          redirectToPortalAction={actions.redirectToPortal}
          isPortalCreationInProgress={isPortalCreationInProgress}
        />
      ) : (
        <StyledContainer>
          <BoardGroup>
            <StyledBoardHeading>
              <LegendText>
                <Text
                  element="h3"
                  size="title3"
                  key="title"
                  value={
                    view === 'organisationSelectionView'
                      ? LABELS.connectYourXeroOrganisation
                      : LABELS.signupToPlanday
                  }
                />
              </LegendText>
            </StyledBoardHeading>

            {view === 'organisationSelectionView' && (
              <OrganisationSelector
                tenants={tenants}
                tenantId={tenantId}
                setTenantIdAction={actions.setTenantId}
                proceedAction={actions.proceed}
                fetchFormDataAction={actions.fetchFormData}
                signupUrl={signupUrl}
              />
            )}

            {view === 'signupFormView' && <SignupForm />}
          </BoardGroup>
        </StyledContainer>
      )}
    </>
  );
}

const mapStateToProps = ({
  xeroSignup,
}: {
  xeroSignup: XeroSignupState<string>;
}) => {
  const {
    hasSaved,
    tenants,
    tenantId,
    view,
    signupUrl,
    isPortalCreationInProgress,
  } = xeroSignup || DEFAULT_STATE;
  return {
    hasSaved,
    tenants,
    tenantId,
    view,
    signupUrl,
    isPortalCreationInProgress,
  };
};

export const mapDispatchToProps = (dispatch: Dispatch<XeroSignupAction>) => ({
  actions: bindActionCreators(actionCreators, dispatch as Dispatch<AnyAction>),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(XeroSignup);
